import React from 'react';
import { Box, BoxProps } from '@mui/material';
import { generateSrcSet, getOptimizedImageFallback } from '../utils/imageUtils';

interface OptimizedImageProps extends Omit<BoxProps, 'component' | 'src' | 'srcSet' | 'sizes' | 'loading'> {
  src: string | null | undefined;
  alt: string;
  isPetShopClient?: boolean;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  fallbackToOriginal?: boolean;
}

/**
 * Componente de imagen optimizada que usa srcset y lazy loading automáticamente
 * Detecta si hay versiones optimizadas (small/medium/large) y las usa,
 * o usa la imagen original como fallback
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  isPetShopClient = false,
  sizes = '(max-width: 600px) 400px, (max-width: 1200px) 800px, 1600px',
  loading = 'lazy',
  fallbackToOriginal = true,
  sx,
  ...otherProps
}) => {
  if (!src) {
    return null;
  }

  const srcSet = generateSrcSet(src, isPetShopClient);
  const fallbackSrc = getOptimizedImageFallback(src, isPetShopClient) || src;

  // Si hay srcset disponible, usar versión optimizada
  if (srcSet) {
    return (
      <Box
        component="img"
        src={fallbackSrc}
        srcSet={srcSet}
        sizes={sizes}
        loading={loading}
        alt={alt}
        sx={sx}
        {...otherProps}
      />
    );
  }

  // Fallback: usar imagen original con lazy loading
  return (
    <Box
      component="img"
      src={fallbackToOriginal ? src : fallbackSrc}
      loading={loading}
      alt={alt}
      sx={sx}
      {...otherProps}
    />
  );
};

export default OptimizedImage;

